import { useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { BRANDING } from '@/features/marketing/content/branding';
import { cn } from '@/lib/cn';

/**
 * The NeonCarts brand lockup: the diamond-and-cart mark plus the wordmark.
 *
 * The wordmark is live text rather than part of the image because the only
 * bundled wordmark artwork (`logo-full.png`) has a near-white "Neon", which
 * disappears against the app's white chrome. Keep image lockups to dark
 * surfaces.
 */

const SIZES = {
  sm: { mark: 'h-8', text: 'text-sm', tagline: 'text-xs' },
  md: { mark: 'h-10', text: 'text-base', tagline: 'text-sm' },
  lg: { mark: 'h-12', text: 'text-2xl', tagline: 'text-base' },
} as const;

const TONES = {
  light: { neon: 'text-ink', carts: 'text-brand-600', slogan: 'text-brand-700' },
  dark: { neon: 'text-white', carts: 'text-brand-400', slogan: 'text-brand-300' },
} as const;

/**
 * The wordmark with the tagline under it, the tagline sized to end exactly
 * where the wordmark does, as on the sign-in artwork.
 *
 * Measured rather than fixed: the two faces load at different times, the
 * ratio differs by size, and the tagline is editable copy. The size class is
 * the starting point and the fallback before the first measure.
 */
function WordmarkWithTagline({
  wordmark,
  className,
}: {
  wordmark: ReactNode;
  className: string;
}) {
  const wordRef = useRef<HTMLSpanElement>(null);
  const tagRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const word = wordRef.current;
    const tag = tagRef.current;
    if (!word || !tag) return;
    let cancelled = false;

    const fit = () => {
      if (cancelled) return;
      tag.style.fontSize = '';
      const target = word.getBoundingClientRect().width;
      const current = tag.getBoundingClientRect().width;
      if (target <= 0 || current <= 0) return;
      const base = parseFloat(getComputedStyle(tag).fontSize);
      tag.style.fontSize = `${(base * target) / current}px`;
    };

    fit();
    // Either face arriving late changes one side of the ratio.
    void document.fonts?.ready.then(fit);
    document.fonts?.addEventListener('loadingdone', fit);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(word);
    return () => {
      cancelled = true;
      document.fonts?.removeEventListener('loadingdone', fit);
      observer?.disconnect();
    };
  }, []);

  return (
    <span className="flex flex-col items-start leading-none">
      <span ref={wordRef}>{wordmark}</span>
      <span ref={tagRef} className={className}>
        {BRANDING.tagline}
      </span>
    </span>
  );
}

export function BrandLogo({
  size = 'md',
  tone = 'light',
  markOnly = false,
  withTagline = false,
  to = '/',
  className,
}: {
  size?: keyof typeof SIZES;
  /** Which surface this sits on, so the wordmark stays readable. */
  tone?: keyof typeof TONES;
  /** Drop the wordmark and render the mark alone - for tight or icon-only spots. */
  markOnly?: boolean;
  /** Set the brand tagline under the wordmark, as on the sign-in artwork. */
  withTagline?: boolean;
  /** Link target, or null for a plain non-interactive lockup. */
  to?: string | null;
  className?: string;
}) {
  const { mark, text, tagline } = SIZES[size];
  const { neon, carts, slogan } = TONES[tone];

  const wordmark = (
    <span className={cn(text, 'font-bold tracking-tight', neon)}>
      {BRANDING.wordmarkStart}
      <span className={carts}>{BRANDING.wordmarkAccent}</span>
    </span>
  );

  const content = (
    <>
      {/* The mark is landscape, so height drives it and width follows. */}
      <img
        src={BRANDING.logoSrc || '/images/logo.png'}
        alt={markOnly ? BRANDING.name : ''}
        className={cn(mark, 'w-auto shrink-0 object-contain')}
        width={744}
        height={582}
      />
      {markOnly ? null : withTagline && BRANDING.tagline ? (
        <WordmarkWithTagline
          wordmark={wordmark}
          className={cn(tagline, '-mt-0.5 font-script font-semibold whitespace-nowrap', slogan)}
        />
      ) : (
        wordmark
      )}
    </>
  );

  const classes = cn('flex items-center gap-2', className);

  if (to === null) {
    return <span className={classes}>{content}</span>;
  }

  return (
    <Link to={to} className={classes}>
      {content}
    </Link>
  );
}
